import React, { useState, useMemo, useEffect } from "react";
import { FaPlus, FaEdit } from "react-icons/fa";
import { ArrowDown, Settings2 } from "lucide-react";
import { BASE_URL } from "utils/constant";
import VendorEmptyIcon from "assets/empty state/VendorEmpty.svg";
import MaterialLibraryUploadModal from "./MaterialLibraryUploadModal";
import { CiImport } from "react-icons/ci";
import { MdInventory, MdEdit } from "react-icons/md";
import { BsFileText } from "react-icons/bs";
import { TbRulerMeasure, TbTag } from "react-icons/tb";
import { FiCalendar, FiUser } from "react-icons/fi";
// Mock data for demonstration

const formatDisplayDate = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};



const MaterialRow = ({ material, onEdit }) => {
  return (
    <div className="group relative flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 py-2 mb-3 bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 hover:border-[#3B0069]/30 overflow-hidden">
      {/* Left Accent Border */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#3B0069] opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

      <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 w-full items-center">
        {/* Material Name & Subcategory */}
        <div className="sm:col-span-3 flex items-start gap-3">
          <div className="p-2 bg-purple-50 rounded-lg text-[#3B0069] mt-1">
            <MdInventory size={20} />
          </div>
          <div>
            <h3 className="text-[16px] font-semibold text-gray-800 leading-tight">
              {material.material_name}
            </h3>
            <p className="text-[13px] text-gray-500 mt-1 font-medium bg-gray-100 inline-block px-2 py-0.5 rounded-md">
              {material.subcategory}
            </p>
          </div>
        </div>

        {/* Specification */}
        <div className="sm:col-span-3 flex items-start gap-2">
          <BsFileText size={16} className="text-gray-400 mt-1 shrink-0" />
          <div className="flex flex-col">
            <span className="text-[12px] text-gray-400 uppercase tracking-wider font-medium">
              Specification
            </span>
            <span className="text-[14px] text-gray-700 line-clamp-2" title={material.specification}>
              {material.specification}
            </span>
          </div>
        </div>

        {/* Unit & Brand */}
        <div className="sm:col-span-2 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <TbRulerMeasure size={16} className="text-gray-400" />
            <span className="text-[14px] text-gray-700 font-medium">
              {material.uom}
            </span>
          </div>
          {material.brand_name && (
            <div className="flex items-center gap-2">
              <TbTag size={16} className="text-gray-400" />
              <span className="text-[13px] text-gray-600">
                {material.brand_name}
              </span>
            </div>
          )}
        </div>

        {/* Created/Updated Info */}
        <div className="sm:col-span-3 flex flex-col gap-1 border-l border-gray-100 pl-4">
          <div className="flex items-center gap-2 text-[12px] text-gray-500">
            <FiUser size={12} />
            <span className="truncate max-w-[120px]" title={material.created_by}>
              {material.created_by}
            </span>
          </div>
          <div className="flex items-center gap-2 text-[12px] text-gray-400">
            <FiCalendar size={12} />
            <span>{formatDisplayDate(material.created_on)}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="sm:col-span-1 flex justify-end">
          <button
            onClick={() => onEdit(material)}
            className="p-2 text-gray-400 hover:text-[#3B0069] hover:bg-purple-50 rounded-full transition-colors duration-200"
            title="Edit Material"
          >
            <MdEdit size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

const CreateMaterialDialog = ({ isOpen, onClose, onSave, editingMaterial }) => {
  const [formData, setFormData] = useState({
    material_name: "",
    specification: "",
    subcategory: "",
    uom: "",
    brand_name: "",
  });
  const uomOptions = [
    "Pieces",
    "Bags",
    "Kg",
    "Liters",
    "Cubic Feet",
    "Meters",
    "Square Feet",
  ];
  const [isCustomUom, setIsCustomUom] = useState(false);
  const [customUom, setCustomUom] = useState("");
  const selectableUomOptions = useMemo(() => {
    // Include a custom UOM into the list so the select can display it
    if (formData.uom && !uomOptions.includes(formData.uom)) {
      return [...uomOptions, formData.uom];
    }
    if (customUom && !uomOptions.includes(customUom)) {
      return [...uomOptions, customUom];
    }
    return uomOptions;
  }, [uomOptions, formData.uom, customUom]);

  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      material_name: editingMaterial?.material_name || "",
      specification: editingMaterial?.specification || "",
      subcategory: editingMaterial?.subcategory || "",
      uom: editingMaterial?.uom || "",
      brand_name: editingMaterial?.brand_name || "",
    });
    // Initialize custom UOM state if existing UOM isn't in presets
    const hasPresetUom =
      editingMaterial?.uom && uomOptions.includes(editingMaterial.uom);
    if (editingMaterial?.uom && !hasPresetUom) {
      setIsCustomUom(true);
      setCustomUom(editingMaterial.uom);
    } else {
      setIsCustomUom(false);
      setCustomUom("");
    }
  }, [isOpen, editingMaterial]);
  const handleSubmit = () => {
    // Basic validation
    const chosenUom = isCustomUom
      ? (customUom || "").trim()
      : (formData.uom || "").trim();
    if (
      !formData.material_name ||
      !formData.specification ||
      !formData.subcategory ||
      !chosenUom
    ) {
      alert("Please fill in all required fields");
      return;
    }

    onSave({
      ...formData,
      uom: chosenUom,
    });
    onClose();
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-[500px] max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-[#232323]">
            {editingMaterial ? "Edit Material" : "Create New Material"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Material Name *
            </label>
            <input
              type="text"
              required
              value={formData.material_name}
              onChange={(e) => handleChange("material_name", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#3B0069]"
              placeholder="Enter material name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Specification *
            </label>
            <textarea
              required
              value={formData.specification}
              onChange={(e) => handleChange("specification", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#3B0069]"
              placeholder="Enter specification details"
              rows="2"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subcategory *
              </label>
              <input
                type="text"
                required
                value={formData.subcategory}
                onChange={(e) => handleChange("subcategory", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#3B0069]"
                placeholder="e.g., Cement, Bricks"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit of Measure *
              </label>
              <div className="space-y-2">
                <select
                  required
                  value={isCustomUom ? "CUSTOM" : formData.uom}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "CUSTOM") {
                      setIsCustomUom(true);
                      setCustomUom(customUom || "");
                    } else {
                      setIsCustomUom(false);
                      handleChange("uom", val);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#3B0069]"
                >
                  <option value="">Select UOM</option>
                  {selectableUomOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                  <option value="CUSTOM">Custom...</option>
                </select>
                {isCustomUom && (
                  <input
                    type="text"
                    required
                    value={customUom}
                    onChange={(e) => setCustomUom(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const trimmed = (customUom || "").trim();
                        if (trimmed) {
                          // Commit custom UOM to the main select and hide the custom field
                          handleChange("uom", trimmed);
                          setIsCustomUom(false);
                        }
                      }
                    }}
                    placeholder="Enter custom UOM"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#3B0069]"
                  />
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Brand Name
            </label>
            <input
              type="text"
              value={formData.brand_name}
              onChange={(e) => handleChange("brand_name", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#3B0069]"
              placeholder="Enter brand name"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="px-6 py-2 bg-[#3B0069] text-white rounded-md hover:bg-[#2d0052]"
            >
              {editingMaterial ? "Update" : "Create"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MaterialLibrary = () => {
  const [materials, setMaterials] = useState([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [organisationId, setOrganisationId] = useState(
    JSON.parse(localStorage.getItem("organization"))?.id
  );
  const [sortRecentDesc, setSortRecentDesc] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const handleEdit = (material) => {
    setEditingMaterial(material);
    setIsCreateDialogOpen(true);
  };

  const handleSave = async (materialData) => {
    if (editingMaterial) {
      // Update existing material
      const res = await fetch(
        `${BASE_URL}/org/material-library/${editingMaterial.id}/update/`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
          body: JSON.stringify({
            ...materialData,
            organization: organisationId,
          }),
        }
      );
      getMaterials();
      //   console.log("Updated Material:", result);
    } else {
      // Create new material

      const res = await fetch(`${BASE_URL}/org/material-library/create/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
        body: JSON.stringify({
          ...materialData,
          organization: organisationId,
        }),
      });
      getMaterials();
      //   console.log("Created Material:", result);
      //   setMaterials((prev) => [result, ...prev]);
    }
    setEditingMaterial(null);
  };

  const handleCloseDialog = () => {
    setIsCreateDialogOpen(false);
    setEditingMaterial(null);
  };

  const getMaterials = async () => {
    const res = await fetch(`${BASE_URL}/org/material-library/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("access")}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ org_id: organisationId }),
    });
    const data = await res.json();
    setMaterials(data);
  };

  useEffect(() => {
    getMaterials();
  }, []);

  const sortedMaterials = useMemo(() => {
    if (!materials || materials.length === 0) return materials;
    return [...materials].sort((a, b) =>
      sortRecentDesc
        ? new Date(b.created_on) - new Date(a.created_on)
        : new Date(a.created_on) - new Date(b.created_on)
    );
  }, [materials, sortRecentDesc]);

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm ml-4 text-gray-600">
          {materials.length} Materials
        </div>
        <div className="flex items-center gap-3">
          <button
            className="flex items-center gap-1 text-sm text-[#3B0069] border border-gray-200 rounded-lg px-3 py-1 bg-[#ECE6F0] hover:bg-[#e0d4e9] transition"
            onClick={() => setShowUploadModal(true)}
          >
            Excel Import
            <CiImport size={18} />
          </button>
          <button
            className="flex items-center gap-1 text-sm text-[#3B0069] border border-gray-200 rounded-full px-3 py-1 bg-[#ECE6F0] hover:bg-[#e0d4e9] transition"
            onClick={() => setSortRecentDesc((p) => !p)}
          >
            Sorted by Recent
            <span>
              <ArrowDown
                size={14}
                className={`${sortRecentDesc ? "" : "rotate-180"} transition-transform`}
              />
            </span>
          </button>

          <div className="w-[28px] h-[28px] rounded-full flex items-center justify-center bg-[#ECE6F0]">
            <Settings2 size={14} />
          </div>

          <button
            onClick={() => setIsCreateDialogOpen(true)}
            className="flex items-center gap-2 bg-[#3B0069] h-[36px] text-white px-4 py-2 rounded-lg hover:bg-[#2d0052] transition"
          >
            <span className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
              <FaPlus size={12} />
            </span>
            <span>Create Material</span>
          </button>
        </div>
      </div>

      <div className="p-2">
        <div
          className="h-[69vh] overflow-y-auto pr-1"
          style={{
            scrollbarWidth: "none",
          }}
        >
          {materials.length > 0 ? (
            sortedMaterials.map((material) => (
              <MaterialRow
                key={material.id}
                material={material}
                onEdit={handleEdit}
              />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-10">
              <img
                src={VendorEmptyIcon}
                draggable={false}
                alt="No Vendors"
                className="mb-4"
                width={240}
              />
              <span className="text-gray-500 text-2xl">No Materials in library</span>
              <span className="text-gray-500 text-sm">
                Add a material to the library now
              </span>
            </div>
          )}
        </div>
      </div>

      <CreateMaterialDialog
        isOpen={isCreateDialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSave}
        editingMaterial={editingMaterial}
      />

      {showUploadModal && (
        <MaterialLibraryUploadModal
          organizationId={organisationId}
          onClose={() => {
            setShowUploadModal(false);
            getMaterials();
          }}
        />
      )}
    </div>
  );
};

export default MaterialLibrary;
